import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserQuery } from "../common/useUserQuery";
import {
  Button,
  Flex,
  Heading,
  TextField,
  Divider,
  SwitchField,
  TextAreaField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import {
  useImagingPostDocumentMutation,
  useImagingPostDocumentMediaMutation,
} from "./api/useImagingAndLabsMutation";
import { StorageManager } from "@aws-amplify/ui-react-storage";
import DatePicker from "rsuite/DatePicker";
import { formatDate } from "../common/useDateFunctions";
import { zodResolver } from "@hookform/resolvers/zod";
const { z } = require("zod");

const acceptedFileTypes = ["image/*", "video/*"];
const documentSchema = z.object({
  documentName: z
    .string()
    .min(1, { message: "Document Name is required" })
    .max(100, { message: "Must be 100 characters or fewer" }),
  documentType: z
    .string()
    .min(1, { message: "Document Type is required" })
    .max(100, { message: "Must be 100 characters or fewer" }),
  documentDate: z.union([
    z.number({ message: "Invalid Format. YYYY-MM-DD Format" }),
    z.string({ message: "Invalid Format. YYYY-MM-DD Format" }),
    z.date({ message: "Invalid Format. YYYY-MM-DD Format" }),
  ]),
  description: z
    .string()
    .min(1, { message: "Description is required" })
    .max(250, { message: "Must be 250 characters or fewer" }),
  active: z.number().int(),
  medias: z.any(),
});

const defaultValues = {
  documentName: "",
  documentType: "",
  documentDate: "",
  description: "",
  active: 1,
  medias: [],
};

export default function ImagingAndLabsAddPage() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();

  const postDocument = useImagingPostDocumentMutation();
  const postDocumentMedia = useImagingPostDocumentMediaMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: zodResolver(documentSchema.required()),
  });

  return (
    <>
      <form
        onSubmit={handleSubmit(async (values) => {
          try {
            const tmpDate = new Date(values.documentDate);
            const documentParams = {
              patientUserId: userQueryData?.patientUserId,
              documentName: values.documentName,
              documentType: values.documentType,
              documentDate: formatDate(tmpDate),
              description: values.description,
              active: values.active,
            };

            const documentResult = await postDocument.mutateAsync(
              documentParams
            );

            if (
              values.medias &&
              values.medias?.length > 0 &&
              documentResult.response.insertedId !== ""
            ) {
              const tmpMedias = values.medias?.filter(
                (media) => typeof media === "string"
              );

              if (tmpMedias.length > 0)
                postDocumentMedia.mutate({
                  documentGroupId: documentResult.response.insertedId,
                  patientUserId: userQueryData?.patientUserId,
                  medias: tmpMedias,
                });
            }
            reset();
            navigate("/user/hr/documents/");
          } catch (error) {
            console.log("Error : ", error);
          }
        })}
      >
        <FlexFormContainer headerTitle={"Imaging and Labs Results"}>
          <Controller
            name="documentName"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Document name"
                {...field}
                value={field.value}
                errorMessage={errors.documentName?.message}
                hasError={!!errors.documentName}
              />
            )}
          />
          <Controller
            name="documentType"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Document type"
                {...field}
                value={field.value}
                errorMessage={errors.documentType?.message}
                hasError={!!errors.documentType}
              />
            )}
          />
          <Label htmlFor="documentDate">Document Date</Label>
          <Controller
            name="documentDate"
            control={control}
            render={({ field }) => (
              <>
                <DatePicker
                  id={field.name}
                  onChange={(value) => field.onChange(value)}
                  value={field.value != null ? new Date(field.value) : null}
                  format="yyyy-MM-dd"
                  oneTap
                  style={{ width: "300px" }}
                  size="lg"
                />
                {!!errors.documentDate && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.documentDate.message}
                  </Text>
                )}
              </>
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextAreaField
                minWidth="300px"
                label="Description"
                {...field}
                value={field.value}
                errorMessage={errors.description?.message}
                hasError={!!errors.description}
              />
            )}
          />
          <Divider
            width="unset"
            height="1px"
            shrink="0"
            alignSelf="stretch"
            size="small"
            orientation="horizontal"
          ></Divider>
          <Heading level="6" children="Attachments"></Heading>
          <Flex
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Controller
              name="medias"
              control={control}
              render={({ field: { onChange, value: frontValue, name } }) => (
                <StorageManager
                  name={name}
                  autoUpload={false}
                  acceptedFileTypes={acceptedFileTypes}
                  path={`public/users/${userQueryData?.patientUserId}/documents/`}
                  maxFileCount={20}
                  onUploadError={(error) => {
                    console.log("Error uploading: ", error);
                  }}
                  onUploadSuccess={({ key }) => {
                    const value = getValues("medias");
                    setValue("medias", [...value, key]);
                  }}
                />
              )}
            />
          </Flex>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <SwitchField
                label="is Active?"
                labelPosition="end"
                {...field}
                isChecked={field.value}
                onChange={(event) =>
                  field.onChange(event.target.checked ? 1 : 0)
                }
              />
            )}
          />

          <Flex gap="12" direction="column">
            <Button variation="primary" type="submit">
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
